import React, { useEffect, useState, useRef } from 'react';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import { Map, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import PlacesCarousel from './PlacesCarousel';
import './TourGuide.css';
import PanToOnActivePlace from './PanToOnActivePlace';
import NavBar from '../Components/NavBar';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom icon for the main location marker
const customIcon = new L.Icon({
	iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
	iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
	shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  	iconSize: [25, 41],
  	iconAnchor: [12, 41],
  	popupAnchor: [0, -41],
  	className: 'custom-red-icon'
});

const wikipediaEndpoint = 'https://en.wikipedia.org/w/api.php'

const getPlacesForLocation = async (lat, lon) => {
	const params = new URLSearchParams({
		"action": "query",
		"prop": "coordinates|pageimages|description|info",
		"inprop": "url",
		"pithumbsize": "144",
		"generator": "geosearch",
		"ggsradius": "5000",
		"ggslimit": "10",
		"ggscoord": `${lat}|${lon}`,
		"format": "json",
		"origin": "*"
	})
	
	const url = `${wikipediaEndpoint}?${params.toString()}`
	const res = await fetch(url, {
		method: 'GET',
		headers: {
			'Content-Type': 'application/json',
		}
	})
	
	if (!res.ok) {
		throw new Error(`HTTP error! status: ${res.status}`)
	}
	
	const data = await res.json()
	
	if (data.query && data.query.pages) {
		return data.query.pages
	}
	return {}
}

const TourGuide = () => {
  const [description, setDescription] = useState("Click 'Get My Location' to start");
  const [currentLocation, setCurrentLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState([51.505, -0.09]);
  const [shouldControlCenter, setShouldControlCenter] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [nearestPlaces, setNearestPlaces] = useState([]);
  const [wikipediaContent, setWikipediaContent] = useState({});
  const [zoom, setZoom] = useState(16);
  const [activePlaceId, setActivePlaceId] = useState(null);
  const [carouselOpen, setCarouselOpen] = useState(true);
  const mapRef = useRef();
  const markerRefs = useRef({});

  useEffect(() => {
	setIsLoading(false)
  }, [nearestPlaces])

  const updateLocation = (lat, lon) => {
	setCurrentLocation([lat, lon]);
	setMapCenter([lat, lon]);
	setShouldControlCenter(true);
	setDescription(`Your Location (${lat.toFixed(4)}, ${lon.toFixed(4)})`);
	updateNearestPlaces(lat, lon)
	setActivePlaceId(null)
  }

  const handleMarkerDrag = (lat, lon) => {
    setCurrentLocation([lat, lon]);
    setMapCenter([lat, lon]);
    setActivePlaceId(null);
    setDescription(`Your Location (${lat.toFixed(4)}, ${lon.toFixed(4)})`);
    updateNearestPlaces(lat, lon);
  }

  
  const recenterMap = () => {
    setActivePlaceId(null);
    if (!currentLocation) return;
    const map = mapRef.current && mapRef.current.leafletElement;
    if (map) {
      map.setView(currentLocation, map.getZoom(), { animate: true });
    }
  };

  const fetchWikipediaContent = async (places) => {
    if (places.length === 0) return;
    
    try {
      // Fetch all Wikipedia content in parallel
      const fetchPromises = places.map(async (place) => {
        try {
          const response = await fetch(
            `https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(place.title)}`
          );
          const data = await response.json();
          return {
            pageid: place.pageid,
            content: data.extract || 'No description available.'
          };
        } catch (error) {
          console.error(`Error fetching content for ${place.title}:`, error);
          return {
            pageid: place.pageid,
            content: 'Unable to load description.'
          };
        }
      });
      
      const results = await Promise.all(fetchPromises);
      
      // Update content state with all results
      const newContent = {};
      results.forEach(result => {
        newContent[result.pageid] = result.content;
      });
      
      setWikipediaContent(prev => ({
        ...prev,
        ...newContent
      }));
    } catch (error) {
      console.error('Error fetching Wikipedia content:', error);
    }
  };

  const updateNearestPlaces = async (lat, lon) => {
	try {
		const places = await getPlacesForLocation(lat, lon)
		const placesArray = Object.values(places)
		setNearestPlaces(placesArray)
		
		// Pre-fetch Wikipedia content for all places
		fetchWikipediaContent(placesArray)
	} catch (error) {
		console.error("Error fetching places:", error)
		setNearestPlaces([])
	}
  }

  const handleMapClick = (e) => {
    setCarouselOpen(false);
    setActivePlaceId(null);
  };

  const handleMarkerClick = (placeId) => {
    setCarouselOpen(true);
    setActivePlaceId(placeId);
  };

  const getCurrentLocation = async () => {
    setIsLoading(true);
    setError("");
    setCarouselOpen(true);
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          updateLocation(latitude, longitude)
        },
        (error) => {
          console.error("Error getting location:", error);
          setError("Unable to get your location. Please try again or enter a location manually.");
          setIsLoading(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      );
    } else {
      setError("Geolocation is not supported by this browser.");
      setIsLoading(false);
    }
  };

  const displayPlaces = () => {
	return (
		<div>
			{nearestPlaces?.map((place) => {
				// Check if place has coordinates before rendering marker
				if (!place.coordinates || !place.coordinates[0]) {
					return null;
				}
				const isActive = place.pageid === activePlaceId;
				return (
					<Marker
						key={place.pageid}
						position={[place.coordinates[0].lat, place.coordinates[0].lon]}
						ref={ref => { markerRefs.current[place.pageid] = ref; }}
						onClick={() => handleMarkerClick(place.pageid)}
					>
						<Popup
							autoOpen={isActive}
							autoPan={false}
							open={isActive}
							onOpen={() => setActivePlaceId(place.pageid)}
						>
							<b>{place.title}</b>
							<div style={{ fontSize: '13px', color: '#666' }}>{place.description}</div>
						</Popup>
					</Marker>
				);
			})}
		</div>
	)
  }

  // Center map and open popup when activePlaceId changes
  useEffect(() => {
    if (!activePlaceId) return;
    setShouldControlCenter(false);
    let attempts = 0;
    function tryOpenPopup() {
      const marker = markerRefs.current[activePlaceId];
      if (marker && marker.leafletElement) {
        setTimeout(() => {
          marker.leafletElement.openPopup();
        }, 250);
      } else if (attempts < 5) {
        attempts++;
        setTimeout(tryOpenPopup, 100);
      } else {
        if (!marker || !marker.leafletElement) console.warn('Marker ref not ready for', activePlaceId);
      }
    }
    tryOpenPopup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePlaceId, nearestPlaces]);

  // Always open carousel when a marker is selected
  useEffect(() => {
    if (activePlaceId) setCarouselOpen(true);
  }, [activePlaceId]);

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <NavBar />
      <center>
        <h1>Tour Guide</h1>
      </center>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={getCurrentLocation}
            disabled={isLoading}
          >
            {isLoading ? 'Getting Location...' : 'Get My Location'}
          </Button>
          <TextField 
            id="location-input" 
            label="Location" 
            value={description}
            onChange={() => {}}
            variant="outlined"
            fullWidth
          />
        </div>
        
        {error && (
          <div style={{ color: 'red', marginBottom: '10px', fontSize: '14px' }}>
            {error}
          </div>
        )}
        
        <div style={{ height: '520px', width: '100%', border: '1px solid #ccc' }}>
            <Map 
              {...(shouldControlCenter ? { center: mapCenter } : {})}
              zoom={zoom} 
              scrollWheelZoom={true}
              onZoom={(e) => setZoom(e.target.getZoom())}
              style={{ height: '100%', width: '100%' }}
              ref={mapRef}
              onClick={handleMapClick}
            >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
			{currentLocation && (
				<Marker
					position={currentLocation}
					draggable={true}
					icon={customIcon}
					onDragEnd={e => handleMarkerDrag(e.target.getLatLng().lat, e.target.getLatLng().lng)}
					onClick={() => { setCarouselOpen(true); recenterMap(); }}
				>
					<Popup>{description}</Popup>
				</Marker>
			)}
			{displayPlaces()}
			<PanToOnActivePlace activePlace={nearestPlaces.find(p => p.pageid === activePlaceId)} />
            </Map>
        </div>
      {carouselOpen && (
        <PlacesCarousel
          places={nearestPlaces.map(place => ({
            ...place,
            wikipediaContent: wikipediaContent[place.pageid] || null
          }))}
          activePlaceId={activePlaceId}
          onSelectPlace={placeId => {
            setActivePlaceId(placeId);
            setCarouselOpen(true);
          }}
        />
      )}
    </div>
  );
};

export default TourGuide;